// DevAdminDashboard.jsx
import React from 'react';
import { Database, Users, Settings, Calendar, ArrowRight, Monitor } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatsCard from './StatsCard';

const DevAdminDashboard = () => {
  const navigate = useNavigate();

  const dashboardCards = [
    {
      id: 'control-users',
      title: 'Control Users',
      description: 'Manage user accounts, permissions, and profiles',
      icon: Users,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-100',
      path: '/dev-admin/users',
      stats: 'View, Create, Edit & Delete Users'
    },
    {
      id: 'control-portal',
      title: 'Portal Control',
      description: 'Manage portal types and configurations for different institutions',
      icon: Monitor,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-100',
      path: '/dev-admin/portal',
      stats: 'Community, University & School Portals'
    },
    {
      id: 'control-groups',
      title: 'Control Groups',
      description: 'Manage user groups, roles, and permissions',
      icon: Settings,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-100',
      path: '/dev-admin/groups',
      stats: 'Group & Role Management'
    }
  ];

  const handleCardClick = (card) => {
    if (card.status) {
      // Handle future features - show notification or disable click
      return;
    }
    navigate(card.path);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dev Admin Dashboard</h1>
        <p className="text-gray-600">Manage users, schemas, and system configurations</p>
      </div>
      
      <div className="space-y-6">
        {/* Stats Overview */}
        <div className="w-full">
          <StatsCard />
        </div>
        
        {/* Main Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dashboardCards.map((card) => {
            const IconComponent = card.icon;
            return (
              <div
                key={card.id}
                onClick={() => handleCardClick(card)}
                className={`bg-white rounded-lg shadow-md p-6 transition-all duration-200 ${
                  card.status 
                    ? 'cursor-not-allowed opacity-75' 
                    : 'cursor-pointer hover:shadow-lg hover:-translate-y-1'
                }`}
              >
                {/* Card Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 ${card.bgColor} rounded-lg`}>
                    <IconComponent className={`h-6 w-6 ${card.iconColor}`} />
                  </div>
                  {!card.status && (
                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                  )}
                  {card.status && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                      {card.status}
                    </span>
                  )}
                </div>

                {/* Card Content */}
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-gray-900">{card.title}</h3>
                  <p className="text-gray-600 text-sm">{card.description}</p>
                  
                  {/* Stats/Features */}
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-sm text-gray-500">{card.stats}</p>
                  </div>
                </div>

                {/* Action Indicator */}
                {!card.status && (
                  <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                    <span>Manage</span>
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick Actions Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-200 rounded">
                  <Database className="h-5 w-5 text-blue-600" />
                </div>
                <span className="font-medium text-gray-700">System Backup</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-500 group-hover:text-gray-700" />
            </button>
            
            <button className="flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors group">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-200 rounded">
                  <Settings className="h-5 w-5 text-green-600" />
                </div>
                <span className="font-medium text-gray-700">System Health</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-500 group-hover:text-gray-700" />
            </button>
            
            <button className="flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors group">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-200 rounded">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <span className="font-medium text-gray-700">User Analytics</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-500 group-hover:text-gray-700" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevAdminDashboard;
